import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut, MessageCircleQuestion, Settings as SettingsIcon } from 'lucide-react';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useLogout, useMe } from '@/api/hooks/useAuth';
import { cn } from '@/lib/utils';

import { HelpDialog } from './HelpDialog';

type ProfileMenuProps = {
  /** Layout style: the sidebar shows the full email + avatar when
   *  expanded, and just the avatar (with a tooltip + popover) when
   *  collapsed. */
  collapsed: boolean;
};

/**
 * User identity affordance pinned to the bottom-left of the sidebar.
 *
 * Renders the user's avatar + email and, on click, a dropdown with
 * Settings / Get help / Log out. "Get help" opens the shared
 * `<HelpDialog>` — the same dialog the topbar Help button opens, so the
 * contact flow lives in exactly one place.
 *
 * Expanded mode: avatar + truncated email, chevron-less trigger so it
 * reads as a sidebar surface rather than a select.
 * Collapsed mode: avatar only, with a tooltip exposing the email.
 */
export function ProfileMenu({ collapsed }: ProfileMenuProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { data: me } = useMe();
  const logout = useLogout();
  const [helpOpen, setHelpOpen] = useState(false);

  const email = me?.email ?? '';
  const initials = email ? email.slice(0, 2).toUpperCase() : '··';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const triggerExpanded = (
    <button
      type="button"
      className="group flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-sidebar-accent focus:outline-none focus-visible:bg-sidebar-accent"
    >
      <Avatar className="h-8 w-8 shrink-0">
        <AvatarFallback className="text-[11px] font-semibold">
          {initials}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div
          className="truncate text-xs font-medium text-sidebar-foreground"
          title={email}
        >
          {email || 'N/A'}
        </div>
        <div className="text-[10px] uppercase tracking-wider text-sidebar-foreground/60">
          {t('profile.accountLabel', { defaultValue: 'Account' })}
        </div>
      </div>
    </button>
  );

  const triggerCollapsed = (
    <button
      type="button"
      aria-label={t('profile.menuLabel', { defaultValue: 'Account menu' })}
      className="flex h-10 w-10 items-center justify-center rounded-md transition-colors hover:bg-sidebar-accent focus:outline-none focus-visible:bg-sidebar-accent"
    >
      <Avatar className="h-8 w-8">
        <AvatarFallback className="text-[11px] font-semibold">
          {initials}
        </AvatarFallback>
      </Avatar>
    </button>
  );

  return (
    <>
      <DropdownMenu>
        {collapsed ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>{triggerCollapsed}</DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={8}>
              {email || t('profile.menuLabel', { defaultValue: 'Account menu' })}
            </TooltipContent>
          </Tooltip>
        ) : (
          <DropdownMenuTrigger asChild>{triggerExpanded}</DropdownMenuTrigger>
        )}
        <DropdownMenuContent
          side={collapsed ? 'right' : 'top'}
          align={collapsed ? 'end' : 'start'}
          sideOffset={collapsed ? 12 : 8}
          className={cn('w-56', collapsed && 'ml-1')}
        >
          {email && (
            <>
              <div className="px-2 py-1.5">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {t('profile.signedInAs', { defaultValue: 'Signed in as' })}
                </div>
                <div className="truncate text-xs font-medium" title={email}>
                  {email}
                </div>
              </div>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem asChild>
            <Link to="/dashboard/settings">
              <SettingsIcon className="mr-2 h-4 w-4" />
              {t('nav.settings')}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              setHelpOpen(true);
            }}
          >
            <MessageCircleQuestion className="mr-2 h-4 w-4" />
            {t('profile.getHelp', { defaultValue: 'Get help' })}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              handleLogout();
            }}
            className="text-destructive focus:bg-destructive/10 focus:text-destructive"
          >
            <LogOut className="mr-2 h-4 w-4" />
            {t('nav.logout')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <HelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
    </>
  );
}
